import { config, isDevelopment } from "../config/environment";
import { loggers } from "../utils/logger";

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface ApiListResponse<T> {
  success: boolean;
  message: string;
  data?: T[];
  total?: number;
  limit?: number;
  summary?: any;
}

class ApiService {
  private baseURL: string;
  private headers: HeadersInit;

  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
  }

  // Public getter for baseURL
  get apiBaseUrl(): string {
    return this.baseURL;
  }

  private async getAuthToken(): Promise<string | null> {
    if (isDevelopment()) {
      // For local development, use mock token or no auth
      return null;
    }
    
    // Get JWT token from Cognito authentication
    try {
      const { fetchAuthSession, getCurrentUser } = await import("aws-amplify/auth");
      
      // First check if user is authenticated 
      const user = await getCurrentUser();
      if (!user) {
        loggers.auth.log("No authenticated user found");
        return null;
      }
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        loggers.auth.error("No ID token found in session");
        return null;
      }
      
      return token;
    } catch (error) {
      loggers.auth.error("Error getting auth token:", error);
      return null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T> | ApiListResponse<T>> {
    const token = await this.getAuthToken();
    const headers = {
      ...this.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const url = `${this.baseURL}${endpoint}`;
    
    try {
      loggers.api.log(`Request: ${url}`);
        if (token) {
        loggers.api.log("Authenticated with JWT token");
      }
      
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorBody = await response.text();
          loggers.api.error(`Error Response:`, errorBody);
          errorMessage += ` - ${errorBody}`;
        } catch (e) {
          // Ignore if we can't read the error body
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      loggers.api.success(`${endpoint}`);
      return data;
    } catch (error) {
      loggers.api.error(`Request failed for ${url}:`, error);
      
      // Check if this is a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          message: "Network error - please check your connection and API endpoint",
          error: "Network connection failed",
        };
      }
      
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error occurred",
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Students endpoints
  async getStudents(params: {
    limit?: number;
    search?: string;
    status?: string;
    includeInactive?: boolean;
  } = {}): Promise<ApiListResponse<any>> {
    const searchParams = new URLSearchParams();
    
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.status) searchParams.append('status', params.status);
    if (params.includeInactive) searchParams.append('includeInactive', 'true');
    
    const queryString = searchParams.toString();
    const endpoint = `/students${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async getStudent(id: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${id}`);
  }

  async createStudent(data: any): Promise<ApiResponse<any>> {
    return this.request('/students', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStudent(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/students/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStudent(id: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${id}`, {
      method: 'DELETE',
    });
  }

  async reactivateStudent(id: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'reactivate' }),
    });
  }

  async createStudentUserAccount(studentId: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${studentId}?action=create-user-account`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async resendStudentInvitation(studentId: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${studentId}?action=resend-invitation`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async disableStudentUserAccount(studentId: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${studentId}?action=disable-user-account`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async enableStudentUserAccount(studentId: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${studentId}?action=enable-user-account`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async markStudentOnboarded(studentId: string): Promise<ApiResponse<any>> {
    return this.request(`/students/${studentId}?action=mark-onboarded`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Lessons endpoints
  async getLessons(params: {
    limit?: number;
    studentId?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    parentRecurringLessonId?: string;
  } = {}): Promise<ApiListResponse<any>> {
    const searchParams = new URLSearchParams();
    
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.studentId) searchParams.append('studentId', params.studentId);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.status) searchParams.append('status', params.status);
    if (params.parentRecurringLessonId) searchParams.append('parentRecurringLessonId', params.parentRecurringLessonId);
    
    const queryString = searchParams.toString();
    const endpoint = `/lessons${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async getLesson(id: string): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${id}`);
  }

  async createLesson(data: any): Promise<ApiResponse<any>> {
    return this.request('/lessons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLesson(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLesson(id: string): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${id}`, {
      method: 'DELETE',
    });
  }

  // Invoices endpoints
  async getInvoices(params: {
    limit?: number;
    studentId?: string;
    status?: string;
  } = {}): Promise<ApiListResponse<any>> {
    const searchParams = new URLSearchParams();
    
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.studentId) searchParams.append('studentId', params.studentId);
    if (params.status) searchParams.append('status', params.status);
    
    const queryString = searchParams.toString();
    const endpoint = `/invoices${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async getInvoice(id: string): Promise<ApiResponse<any>> {
    return this.request(`/invoices/${id}`);
  }

  async createInvoice(data: any): Promise<ApiResponse<any>> {
    return this.request('/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoice(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteInvoice(id: string): Promise<ApiResponse<any>> {
    return this.request(`/invoices/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkUpdateInvoices(data: { invoiceIds: string[], updates: any, requestId?: string }): Promise<ApiResponse<any>> {
    return this.request(`/invoices/bulk-update`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Tutor Profiles endpoints
  async getTutorProfiles(params: {
    limit?: number;
    userId?: string;
  } = {}): Promise<ApiListResponse<any>> {
    const searchParams = new URLSearchParams();
    
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.userId) searchParams.append('userId', params.userId);
    
    const queryString = searchParams.toString();
    const endpoint = `/tutor-profiles${queryString ? `?${queryString}` : ''}`;
    
    return this.request(endpoint);
  }

  async getTutorProfile(id: string): Promise<ApiResponse<any>> {
    return this.request('/profile');
  }

  async createTutorProfile(data: any): Promise<ApiResponse<any>> {
    return this.request('/profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTutorProfile(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTutorProfile(id: string): Promise<ApiResponse<any>> {
    return this.request(`/tutor-profiles/${id}`, {
      method: 'DELETE',
    });
  }

  // Profile endpoints (for current user's profile)
  async getProfile(): Promise<ApiResponse<any>> {
    return this.request('/profile');
  }

  async acceptTerms(termsVersion: string): Promise<ApiResponse<any>> {
    return this.request('/profile/accept-terms', {
      method: 'POST',
      body: JSON.stringify({ termsVersion }),
    });
  }

  async createProfile(data: any): Promise<ApiResponse<any>> {
    return this.request('/profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProfile(data: any): Promise<ApiResponse<any>> {
    return this.request('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Location endpoints
  async getLocations(): Promise<ApiListResponse<any>> {
    return this.request('/locations');
  }

  async getLocation(id: string): Promise<ApiResponse<any>> {
    return this.request(`/locations/${id}`);
  }

  async createLocation(data: any): Promise<ApiResponse<any>> {
    return this.request('/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLocation(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLocation(id: string): Promise<ApiResponse<any>> {
    return this.request(`/locations/${id}`, {
      method: 'DELETE',
    });
  }

  // Location availability endpoints
  async getLocationAvailability(locationId: string, params: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ApiListResponse<any>> {
    const searchParams = new URLSearchParams();
    searchParams.append('locationId', locationId);
    
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    
    const queryString = searchParams.toString();
    return this.request(`/location-availability?${queryString}`);
  }

  async createLocationAvailability(locationId: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/location-availability?locationId=${locationId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLocationAvailability(id: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/location-availability/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLocationAvailability(id: string): Promise<ApiResponse<any>> {
    return this.request(`/location-availability/${id}`, {
      method: 'DELETE',
    });
  }

  // Predefined calendars endpoints
  async getPredefinedCalendars(): Promise<ApiListResponse<any>> {
    return this.request('/predefined-calendars');
  }

  async getCalendarEvents(calendarId: string): Promise<ApiListResponse<any>> {
    return this.request(`/predefined-calendars/${calendarId}/events`);
  }

  async getCalendarEventsForDateRange(params: {
    startDate?: string;
    endDate?: string;
    locationId?: string;
  } = {}): Promise<ApiListResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.locationId) queryParams.append('locationId', params.locationId);
    
    const endpoint = `/calendar/events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.request(endpoint);
  }

  // New lesson series and cancellation endpoints
  async createLessonSeries(data: any): Promise<ApiResponse<any>> {
    return this.request('/lessons/series', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async cancelLessonInstance(lessonId: string, data: { date: string; reason?: string }): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${lessonId}/cancel-instance`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createLessonInstance(lessonId: string, data: { 
    date: string; 
    notes?: string; 
    homework?: string; 
    pieces?: string[];
    status?: 'scheduled' | 'completed' | 'cancelled';
    reason?: string;
  }): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${lessonId}/create-instance`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async resolveLessonInstance(lessonId: string, date: string): Promise<ApiResponse<{
    resolvedLessonId: string;
    action: 'direct' | 'found_existing' | 'created_new';
    message: string;
    instanceData?: any;
  }>> {
    const response = await this.request(`/lessons/${lessonId}/resolve-instance`, {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    
    // This endpoint returns a single object, so cast to ApiResponse
    return response as ApiResponse<{
      resolvedLessonId: string;
      action: 'direct' | 'found_existing' | 'created_new';
      message: string;
      instanceData?: any;
    }>;
  }

  async cancelLessonSeries(lessonId: string): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${lessonId}/series`, {
      method: 'DELETE'
    });
  }

  async restoreLesson(lessonId: string): Promise<ApiResponse<any>> {
    return this.request(`/lessons/${lessonId}/restore`, {
      method: 'POST'
    });
  }

  // Get term events for recurring lesson end dates, for the tutor's state.
  async getTermEvents(region?: string | null): Promise<ApiResponse<any>> {
    try {
      const { getStateCalendarTypes } = await import('../utils/state-calendars');
      const spec = getStateCalendarTypes(region);
      // Use predefined calendars API instead of calendar events to avoid date range limits
      const response = await this.request('/predefined-calendars');
      
      if (response.success && response.data && (response.data as any).calendars) {
        const calendars = (response.data as any).calendars;
        
        // Collect events from this state's school terms and holidays, all years.
        const allEvents: any[] = [];
        
        const schoolTermsCalendars = calendars.filter((calendar: any) =>
          calendar.calendarType === spec.terms && calendar.isCurrent !== false
        );
        for (const schoolTermsCalendar of schoolTermsCalendars) {
          const termEvents = (schoolTermsCalendar.events || [])
            .filter((event: any) => event.type === 'term')
            .map((event: any) => ({ ...event, id: event.id || event.name, calendarType: 'term' }));
          allEvents.push(...termEvents);
        }
        
        const schoolHolidaysCalendars = calendars.filter((calendar: any) =>
          calendar.calendarType === spec.holidays && calendar.isCurrent !== false
        );
        for (const schoolHolidaysCalendar of schoolHolidaysCalendars) {
          const holidayEvents = (schoolHolidaysCalendar.events || [])
            .filter((event: any) => event.type === 'school_holiday' || event.type === 'holiday')
            .map((event: any) => ({ ...event, id: event.id || event.name, calendarType: 'holiday' }));
          allEvents.push(...holidayEvents);
        }
        
        if (allEvents.length > 0) {
          // Sort by end date
          allEvents.sort((a: any, b: any) => 
            new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
          );
          
          return {
            success: true,
            message: 'Term events retrieved successfully',
            data: allEvents
          };
        }
      }
      
      // Fallback if no term events found
      return {
        success: false,
        message: 'No term events found',
        data: []
      };
    } catch (error) {
      console.error('Error fetching term events:', error);
      return {
        success: false,
        message: 'Failed to fetch term events',
        data: []
      };
    }
  }

  // Family API methods
  async getFamilies(params: {
    limit?: number;
    search?: string;
  } = {}): Promise<ApiListResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);

      const endpoint = `/families${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return this.request(endpoint);
    } catch (error) {
      console.error('Error fetching families:', error);
      return {
        success: false,
        message: 'Failed to fetch families',
        data: []
      };
    }
  }

  async getFamily(id: string): Promise<ApiResponse<any>> {
    try {
      return this.request(`/families/${id}`);
    } catch (error) {
      console.error('Error fetching family:', error);
      return {
        success: false,
        message: 'Failed to fetch family'
      };
    }
  }

  async createFamily(familyData: {
    familyName: string;
    address?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return this.request('/families', {
        method: 'POST',
        body: JSON.stringify(familyData),
      });
    } catch (error) {
      console.error('Error creating family:', error);
      return {
        success: false,
        message: 'Failed to create family'
      };
    }
  }

  async updateFamily(id: string, familyData: {
    familyName?: string;
    address?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return this.request(`/families/${id}`, {
        method: 'PUT',
        body: JSON.stringify(familyData),
      });
    } catch (error) {
      console.error('Error updating family:', error);
      return {
        success: false,
        message: 'Failed to update family'
      };
    }
  }

  async deleteFamily(id: string): Promise<ApiResponse<any>> {
    return this.request(`/families/${id}`, {
      method: 'DELETE',
    });
  }

  // Musical Pieces API methods
  async getMusicalPieces(params: {
    instrument?: string;
  } = {}): Promise<ApiListResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.instrument) queryParams.append('instrument', params.instrument);

      const endpoint = `/musical-pieces${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return this.request(endpoint);
    } catch (error) {
      console.error('Error fetching musical pieces:', error);
      return {
        success: false,
        message: 'Failed to fetch musical pieces',
        data: []
      };
    }
  }

  // Carers are now embedded in families, no separate API methods needed

  // Carer methods removed - carers are now embedded in families

  // Generic method for custom API calls
  async customRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    try {
      return await this.request(endpoint, options);
    } catch (error) {
      console.error('Error in custom request:', error);
      throw error;
    }
  }

  /** For endpoints that return a non-JSON body, such as the CSV report exports. */
  async customRequestText(endpoint: string, options: RequestInit = {}): Promise<string> {
    const token = await this.getAuthToken();
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${body}`);
    }

    return response.text();
  }
}

export const apiService = new ApiService(); 